import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const KEY = "snaplist.edgarButton";
const SIZE = 44;
const PAD = 8;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const fit = (p) => ({ ...p, x: clamp(p.x, PAD, window.innerWidth - SIZE - PAD), y: clamp(p.y, PAD + 56, window.innerHeight - SIZE - PAD) });
const start = () => {
  const d = { x: window.innerWidth - SIZE - 16, y: window.innerHeight - SIZE - 16, tucked: null };
  try { return fit({ ...d, ...JSON.parse(localStorage.getItem(KEY)) }); } catch { return d; }
};

const TAPPABLE = "button, a, input, select, textarea, [role='button'], [role='switch'], [role='checkbox']";
// Something tappable under the button at height y? Returns its top edge, or null.
const blockerTop = (x, y, self) => {
  for (const [px, py] of [[x + SIZE / 2, y + SIZE / 2], [x + 4, y + 4], [x + SIZE - 4, y + 4], [x + 4, y + SIZE - 4], [x + SIZE - 4, y + SIZE - 4]]) {
    const el = document.elementsFromPoint(px, py).find((n) => !self.contains(n) && n.closest(TAPPABLE) && !n.closest("[data-edgar-ignore]"));
    if (el) return el.closest(TAPPABLE).getBoundingClientRect().top;
  }
  return null;
};

// Small round Edgar button: tap to open, drag anywhere, flick to a side edge to tuck it into a tab.
// It slides up out of the way when it would cover a button or link underneath.
export default function EdgarButton({ onOpen }) {
  const [pos, setPos] = useState(start);
  const [lift, setLift] = useState(0);
  const self = useRef(null);
  const drag = useRef(null);
  const save = (p) => { const f = fit(p); setPos(f); localStorage.setItem(KEY, JSON.stringify(f)); };
  useEffect(() => {
    const onResize = () => setPos((p) => fit(p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    if (pos.tucked) return undefined;
    let raf = 0;
    const check = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!self.current || drag.current) return;
        let y = pos.y;
        for (let i = 0; i < 10; i += 1) {
          const top = blockerTop(pos.x, y, self.current);
          if (top == null) break;
          y = Math.max(PAD + 56, top - SIZE - 8);
          if (y === PAD + 56) break;
        }
        setLift((l) => (Math.abs(l - (pos.y - y)) < 6 ? l : pos.y - y));
      });
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    const t = setInterval(check, 1200);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("scroll", check); clearInterval(t); };
  }, [pos.x, pos.y, pos.tucked]);

  const down = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    if (!d.moved && Math.hypot(dx, dy) < 6) return;
    d.moved = true;
    setPos((p) => ({ ...p, x: d.ox + dx, y: d.oy + dy }));
  };
  const up = (e) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.moved) { onOpen(); return; }
    const x = d.ox + e.clientX - d.sx;
    const y = d.oy + e.clientY - d.sy;
    const edge = x < 20 ? "left" : x > window.innerWidth - SIZE - 20 ? "right" : null;
    if (edge) drag.tuckedAt = Date.now();
    save({ x, y, tucked: edge });
  };

  if (pos.tucked) {
    const left = pos.tucked === "left";
    return (
      <button type="button" onClick={() => { if (Date.now() - (drag.tuckedAt || 0) > 400) save({ ...pos, x: left ? PAD + 24 : window.innerWidth - SIZE - PAD - 24, tucked: null }); }}
        aria-label="Bring Edgar back" data-testid="edgar-tab"
        className={`fixed z-40 flex h-14 w-5 items-center justify-center bg-slate-900/85 text-white shadow-md transition-[width,background-color] hover:w-7 hover:bg-[#0053DA] ${left ? "left-0 rounded-r-lg" : "right-0 rounded-l-lg"}`}
        style={{ top: pos.y - 6 }}>
        {left ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
      </button>
    );
  }
  return (
    <button ref={self} type="button" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { drag.current = null; }}
      aria-label="Ask Edgar for help (drag to move, push to the side to tuck away)" title="Ask Edgar · drag to move · push to the edge to tuck away" data-testid="edgar-open" data-lift={lift}
      className={`fixed z-40 inline-flex touch-none select-none items-center justify-center rounded-full bg-[#0053DA] font-serif text-lg font-bold italic text-white shadow-lg shadow-slate-900/25 ring-2 ring-white ${drag.current ? "" : "transition-[top] duration-300"} active:scale-95`}
      style={{ left: pos.x, top: drag.current ? pos.y : pos.y - lift, width: SIZE, height: SIZE }}>
      E
    </button>
  );
}
